/**
 * Course Writing Page (formerly Review Quest Page)
 * Main journey map showing the 4 gates: Translation, Combine, Expansion, Essay
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useTokens } from '../../contexts/TokenContext';
import { useAuth } from '../../contexts/AuthContext';
import {
  GateType,
  QuestProgress,
  GATE_CONFIGS,
  DiagnosticReport,
} from '../../types/writing';
import {
  getQuestProgress,
  startQuest,
  generateDiagnosticReport,
  getTotalTokenCost,
  isGateAccessible,
  retryCourseQuest,
} from '../../services/writingService';
import { coursesService } from '../../services/coursesService';
import '../learning/SentenceNav.css';
import './CourseWritingPage.css';

const LTokenIcon: React.FC<{ size?: number }> = ({ size = 14 }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke="#10b981"
    strokeWidth="2"
    aria-hidden="true"
    style={{ display: 'inline-block', verticalAlign: 'middle', marginRight: 2 }}
  >
    <circle cx="12" cy="12" r="10"/>
    <circle cx="12" cy="12" r="7" strokeOpacity="0.4" strokeWidth="1"/>
    <path d="M9 7v10h6" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);

// Gate type to i18n key mapping
const GATE_I18N_KEYS: Record<GateType, { name: string; desc: string }> = {
  translation: { name: 'translateToEnglish', desc: 'translateToEnglish' },
  combine: { name: 'sentenceCombining', desc: 'sentenceCombiningDesc' },
  expansion: { name: 'paragraphExpansion', desc: 'paragraphExpansionDesc' },
  essay: { name: 'essayWriting', desc: 'essayWritingDesc' },
};

const CourseWritingPage: React.FC = () => {
  const { courseId } = useParams<{ courseId: string }>();
  const navigate = useNavigate();
  const { t } = useTranslation(['writing', 'common']);
  const { stats: tokenStats, canAfford, refreshStats } = useTokens();
  const { isAuthenticated } = useAuth();

  const [progress, setProgress] = useState<QuestProgress | null>(null);
  const [courseName, setCourseName] = useState<string>('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [diagnosticReport, setDiagnosticReport] = useState<DiagnosticReport | null>(null);
  const [showDiagnostic, setShowDiagnostic] = useState(false);
  const [generatingReport, setGeneratingReport] = useState(false);
  const [retrying, setRetrying] = useState(false);

  // Load quest progress and course info
  const loadProgress = useCallback(async () => {
    if (!courseId) return;

    setLoading(true);
    setError(null);

    try {
      // Fetch course info and quest progress in parallel
      const [courseData, questData] = await Promise.all([
        coursesService.getCourse(courseId).catch(() => null),
        getQuestProgress(courseId).catch((err) => {
          // 404 means no quest started yet, which is okay
          if (err.response?.status === 404) return null;
          throw err;
        }),
      ]);

      if (courseData) {
        setCourseName(courseData.title);
      }

      if (questData) {
        setProgress(questData);
        // If quest is completed, load diagnostic report
        if (questData.completedAt && questData.diagnosticReport) {
          setDiagnosticReport(questData.diagnosticReport);
        }
      }
    } catch (err: any) {
      setError(err.message || 'Failed to load quest progress');
    } finally {
      setLoading(false);
    }
  }, [courseId]);

  useEffect(() => {
    loadProgress();
  }, [loadProgress]);

  // Start new quest
  const handleStartQuest = async () => {
    if (!courseId || !canAfford(getTotalTokenCost())) return;

    try {
      const newProgress = await startQuest(courseId);
      setProgress(newProgress);
      await refreshStats();
    } catch (err: any) {
      setError(err.message || 'Failed to start quest');
    }
  };

  // Navigate to gate (also allow clicking completed gates for review)
  const handleGateClick = (gateType: GateType) => {
    if (!progress) return;

    const status = progress.gateProgress[gateType];
    // Allow navigation if gate is available, in_progress, or completed
    if (status === 'locked') return;

    navigate(`/courses/${courseId}/writing/${gateType}`);
  };

  // Handle retry quest (same questions)
  const handleRetryQuest = async () => {
    if (!courseId || retrying) return;

    setRetrying(true);
    try {
      const newProgress = await retryCourseQuest(courseId);
      setProgress(newProgress);
      await refreshStats();
    } catch (err: any) {
      setError(err.message || 'Failed to retry quest');
    } finally {
      setRetrying(false);
    }
  };

  // Generate diagnostic report
  const handleGenerateDiagnostic = async () => {
    if (!courseId) return;

    setGeneratingReport(true);
    try {
      const report = await generateDiagnosticReport(courseId);
      setDiagnosticReport(report);
      setShowDiagnostic(true);
    } catch (err: any) {
      setError(err.message || 'Failed to generate diagnostic report');
    } finally {
      setGeneratingReport(false);
    }
  };

  // Render gate card
  const renderGateCard = (gateType: GateType, index: number) => {
    const config = GATE_CONFIGS.find(g => g.type === gateType);
    if (!config) return null;

    const status = progress?.gateProgress[gateType] || 'locked';
    const score = progress?.scores[gateType];
    const bestScore = progress?.bestScores?.[gateType];
    const accessible = progress ? isGateAccessible(gateType, progress) : false;
    // Also allow clicking completed gates
    const clickable = accessible || status === 'completed';

    return (
      <div
        key={gateType}
        className={`quest-gate quest-gate--${status} ${clickable ? 'quest-gate--accessible' : ''}`}
        onClick={() => clickable && handleGateClick(gateType)}
        role="button"
        tabIndex={clickable ? 0 : -1}
        onKeyPress={(e) => e.key === 'Enter' && clickable && handleGateClick(gateType)}
      >
        {/* Connection line to previous gate */}
        {index > 0 && <div className="quest-gate__connector" />}

        <div className="quest-gate__icon">
          {status === 'completed' ? (
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#16a34a" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M20 6L9 17l-5-5"/></svg>
          ) : status === 'locked' ? (
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="11" width="18" height="11" rx="2"/><path d="M7 11V7a5 5 0 0110 0v4"/></svg>
          ) : (
            <span className="quest-gate__icon-svg">{config.icon}</span>
          )}
        </div>

        <div className="quest-gate__content">
          <h3 className="quest-gate__name">
            {t(GATE_I18N_KEYS[config.type].name)}
            <span className="quest-gate__name-en">{config.name}</span>
          </h3>

          <p className="quest-gate__description">{t(GATE_I18N_KEYS[config.type].desc)}</p>

          <div className="quest-gate__meta">
            <span className="quest-gate__cost">
              <LTokenIcon size={14} />
              {config.tokenCost} {t('tokens', { ns: 'common' })}
            </span>
            <span className="quest-gate__reward">
              <svg className="xp-icon" width="14" height="14" viewBox="0 0 24 24" fill="#eab308" stroke="#eab308" strokeWidth="1"><polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"/></svg>
              {config.xpReward} XP
            </span>
          </div>

          {score !== undefined && (
            <div className={`quest-gate__score quest-gate__score--${score >= 60 ? 'pass' : 'fail'}`}>
              {t('score')}: {score}%
              {bestScore !== undefined && bestScore !== score && (
                <span className="quest-gate__best-score">
                  ({t('bestScore')}: {bestScore}%)
                </span>
              )}
            </div>
          )}
        </div>

        {status === 'available' && (
          <div className="quest-gate__action">
            <span className="quest-gate__play">{t('start')}</span>
          </div>
        )}

        {status === 'completed' && (
          <div className="quest-gate__action">
            <span className="quest-gate__view">{t('reviewResults')}</span>
          </div>
        )}
      </div>
    );
  };

  // Loading state
  if (loading) {
    return (
      <div className="quest-page quest-page--loading">
        <div className="quest-loading">
          <div className="quest-loading__spinner" />
          <p>{t('loading', { ns: 'common' })}</p>
        </div>
      </div>
    );
  }

  // Not authenticated
  if (!isAuthenticated) {
    return (
      <div className="quest-page quest-page--auth">
        <div className="quest-auth">
          <h2>{t('loginRequired')}</h2>
          <p>{t('loginToStart')}</p>
          <Link to="/login" className="quest-auth__btn">
            {t('login', { ns: 'common' })}
          </Link>
        </div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="quest-page quest-page--error">
        <div className="quest-error">
          <h3>{t('error', { ns: 'common' })}</h3>
          <p>{error}</p>
          <button onClick={loadProgress} className="quest-error__retry">
            {t('retry', { ns: 'common' })}
          </button>
        </div>
      </div>
    );
  }

  // No quest started yet
  if (!progress) {
    const totalCost = getTotalTokenCost();
    const canStart = canAfford(totalCost);

    return (
      <div className="quest-page quest-page--start">
        <div className="quest-start">
          <div className="quest-start__header">
            <button className="quest-header__back" onClick={() => navigate(`/courses/${courseId}`)}>
              ← {t('backToCourse')}
            </button>
            {courseName && (
              <div className="quest-start__course-name">{courseName}</div>
            )}
            <h1 className="quest-start__title">
              {t('title')}
              <span className="quest-start__subtitle">{t('subtitle')}</span>
            </h1>
          </div>

          <div className="quest-start__journey">
            <div className="quest-start__gates">
              {GATE_CONFIGS.map((config, index) => (
                <div key={config.type} className="quest-preview-gate">
                  {index > 0 && <div className="quest-preview-gate__line" />}
                  <div className="quest-preview-gate__icon">{config.icon}</div>
                  <div className="quest-preview-gate__info">
                    <span className="quest-preview-gate__name">{t(GATE_I18N_KEYS[config.type].name)}</span>
                    <span className="quest-preview-gate__cost">
                      <LTokenIcon size={12} /> {config.tokenCost} | <svg className="xp-icon" width="12" height="12" viewBox="0 0 24 24" fill="#eab308" stroke="#eab308" strokeWidth="1"><polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"/></svg> {config.xpReward}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="quest-start__summary">
            <div className="quest-start__cost">
              <span className="quest-start__label">{t('totalCost')}:</span>
              <span className="quest-start__value">
                <LTokenIcon size={14} /> {totalCost} {t('tokens', { ns: 'common' })}
              </span>
            </div>
            <div className="quest-start__balance">
              <span className="quest-start__label">{t('yourBalance')}:</span>
              <span className={`quest-start__value ${canStart ? '' : 'quest-start__value--low'}`}>
                <LTokenIcon size={14} /> {tokenStats?.balance || 0}
              </span>
            </div>
          </div>

          <button
            className={`quest-start__btn ${!canStart ? 'quest-start__btn--disabled' : ''}`}
            onClick={handleStartQuest}
            disabled={!canStart}
          >
            {canStart ? t('startJourney') : t('notEnoughTokens')}
          </button>

          {!canStart && (
            <Link to="/tokens" className="quest-start__get-tokens">
              {t('getMoreTokens')}
            </Link>
          )}
        </div>
      </div>
    );
  }

  // Quest in progress or completed
  const allGatesCompleted = Object.values(progress.gateProgress).every(s => s === 'completed');

  // Find the index of the first available/in_progress gate for navigation highlight
  const gateTypes: GateType[] = ['translation', 'combine', 'expansion', 'essay'];
  const currentGateIndex = gateTypes.findIndex(g => {
    const status = progress.gateProgress[g];
    return status === 'available' || status === 'in_progress';
  });
  const activeGateIdx = currentGateIndex >= 0 ? currentGateIndex : gateTypes.length - 1;

  return (
    <div className="quest-page">
      {/* Header - matches learning-mode-header pattern */}
      <div className="learning-mode-header">
        <button className="quest-back-btn" onClick={() => navigate(`/courses/${courseId}`)}>
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M19 12H5"/><path d="M12 19l-7-7 7-7"/></svg>
        </button>
        <h2 className="learning-mode-header__title">{t('title')}</h2>
        <div className="learning-mode-header__nav">
          <button
            className="sentence-nav-btn"
            onClick={() => {
              const prevGate = gateTypes[activeGateIdx - 1];
              if (prevGate) handleGateClick(prevGate);
            }}
            disabled={activeGateIdx === 0}
          >
            ◀
          </button>
          <span className="sentence-nav-counter">{activeGateIdx + 1} / {gateTypes.length}</span>
          <button
            className="sentence-nav-btn"
            onClick={() => {
              const nextGate = gateTypes[activeGateIdx + 1];
              if (nextGate) handleGateClick(nextGate);
            }}
            disabled={activeGateIdx >= gateTypes.length - 1}
          >
            ▶
          </button>
        </div>
      </div>

      {/* Journey Map */}
      <div className="quest-scroll-area">
      <div className="quest-journey">
        <div className="quest-journey__path">
          {gateTypes.map((gate, index) =>
            renderGateCard(gate, index)
          )}
        </div>

        {/* Completion area */}
        {allGatesCompleted && (
          <div className="quest-completion">
            <div className="quest-completion__trophy">
              <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="#667eea" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><path d="M6 9H4.5a2.5 2.5 0 010-5H6"/><path d="M18 9h1.5a2.5 2.5 0 000-5H18"/><path d="M4 22h16"/><path d="M10 14.66V17c0 .55-.47.98-.97 1.21C7.85 18.75 7 20 7 22"/><path d="M14 14.66V17c0 .55.47.98.97 1.21C16.15 18.75 17 20 17 22"/><path d="M18 2H6v7a6 6 0 1012 0V2z"/></svg>
            </div>
            <h2 className="quest-completion__title">{t('questComplete')}</h2>
            <p className="quest-completion__message">{t('congratulations')}</p>

            <div className="quest-completion__actions">
              <button
                className="quest-completion__retry-btn"
                onClick={handleRetryQuest}
                disabled={retrying}
              >
                {retrying ? t('loading', { ns: 'common' }) : t('retryQuest')}
              </button>

              {!diagnosticReport && (
                <button
                  className="quest-completion__diagnostic-btn"
                  onClick={handleGenerateDiagnostic}
                  disabled={generatingReport}
                >
                  {generatingReport ? t('generating') : t('getDiagnosticReport')}
                </button>
              )}

              {diagnosticReport && (
                <button
                  className="quest-completion__view-report-btn"
                  onClick={() => setShowDiagnostic(true)}
                >
                  {t('viewDiagnosticReport')}
                </button>
              )}
            </div>
          </div>
        )}
      </div>
      </div>

      {/* Diagnostic Report Modal */}
      {showDiagnostic && diagnosticReport && (
        <div className="quest-diagnostic-modal">
          <div className="quest-diagnostic-modal__overlay" onClick={() => setShowDiagnostic(false)} />
          <div className="quest-diagnostic-modal__content">
            <button
              className="quest-diagnostic-modal__close"
              onClick={() => setShowDiagnostic(false)}
            >
              ×
            </button>

            <h2 className="quest-diagnostic__title">{t('diagnosticReport')}</h2>

            <div className="quest-diagnostic__score">
              <div className="quest-diagnostic__score-circle">
                <span className="quest-diagnostic__score-value">{diagnosticReport.overallScore}</span>
                <span className="quest-diagnostic__score-label">{t('overallScore')}</span>
              </div>
            </div>

            <div className="quest-diagnostic__progress">
              <h3>{t('skillProgress')}</h3>
              <div className="quest-diagnostic__bars">
                <div className="quest-diagnostic__bar">
                  <span className="quest-diagnostic__bar-label">{t('vocabulary')}</span>
                  <div className="quest-diagnostic__bar-track">
                    <div
                      className="quest-diagnostic__bar-fill"
                      style={{ width: `${diagnosticReport.progressSummary.vocabularyMastery}%` }}
                    />
                  </div>
                  <span className="quest-diagnostic__bar-value">
                    {diagnosticReport.progressSummary.vocabularyMastery}%
                  </span>
                </div>
                <div className="quest-diagnostic__bar">
                  <span className="quest-diagnostic__bar-label">{t('grammar')}</span>
                  <div className="quest-diagnostic__bar-track">
                    <div
                      className="quest-diagnostic__bar-fill"
                      style={{ width: `${diagnosticReport.progressSummary.grammarAccuracy}%` }}
                    />
                  </div>
                  <span className="quest-diagnostic__bar-value">
                    {diagnosticReport.progressSummary.grammarAccuracy}%
                  </span>
                </div>
                <div className="quest-diagnostic__bar">
                  <span className="quest-diagnostic__bar-label">{t('writing')}</span>
                  <div className="quest-diagnostic__bar-track">
                    <div
                      className="quest-diagnostic__bar-fill"
                      style={{ width: `${diagnosticReport.progressSummary.writingFluency}%` }}
                    />
                  </div>
                  <span className="quest-diagnostic__bar-value">
                    {diagnosticReport.progressSummary.writingFluency}%
                  </span>
                </div>
                <div className="quest-diagnostic__bar">
                  <span className="quest-diagnostic__bar-label">{t('connectors')}</span>
                  <div className="quest-diagnostic__bar-track">
                    <div
                      className="quest-diagnostic__bar-fill"
                      style={{ width: `${diagnosticReport.progressSummary.connectorUsage}%` }}
                    />
                  </div>
                  <span className="quest-diagnostic__bar-value">
                    {diagnosticReport.progressSummary.connectorUsage}%
                  </span>
                </div>
              </div>
            </div>

            <div className="quest-diagnostic__diagnosis">
              <h3>{t('diagnosis')}</h3>
              <p>{diagnosticReport.overallDiagnosis}</p>
            </div>

            {diagnosticReport.knowledgePointAnalysis.weakPoints.length > 0 && (
              <div className="quest-diagnostic__weak-points">
                <h3>{t('areasToImprove')}</h3>
                <ul>
                  {diagnosticReport.knowledgePointAnalysis.weakPoints.map((point, index) => (
                    <li key={index} className="quest-diagnostic__weak-point">
                      <span className="quest-diagnostic__weak-point-name">
                        {point.specificPointChinese}
                      </span>
                      <span className="quest-diagnostic__weak-point-desc">
                        {point.description}
                      </span>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {diagnosticReport.recommendedLessons.length > 0 && (
              <div className="quest-diagnostic__recommendations">
                <h3>{t('recommendations')}</h3>
                <ul>
                  {diagnosticReport.recommendedLessons.map((lesson, index) => (
                    <li key={index}>{lesson}</li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default CourseWritingPage;
